/**
 * Input-request schemas. PRD-005 input-requests DATA-IR-02/IR-03, §5
 * TECH-IR-01/IR-03; ARCH-028 §1/§2.
 *
 * One operator-composed, recipient-answered structured Q&A. The operator
 * authors `questions` (JSONB array); the recipient fills `answers` (JSONB keyed
 * by question id). Both are stored JSONB carrying a single sibling version
 * (`questions_schema_version` on the `input_requests` row, migration 0033);
 * `answers` reuses that version because answers are validated against
 * `questions` at the RPC boundary (DATA-IR-03 / migration 0035
 * submit_input_response).
 *
 * Four question kinds (locked 2026-06-02 design pass): short_text, long_text,
 * single_choice, multi_choice. An answer is `string | string[]`: the array form
 * is multi_choice, every other kind is a string (single_choice = the chosen
 * option). Each question carries an optional one-line `hint`.
 *
 * These schemas are the SAME object that validates both the HTTP endpoint and
 * the MCP tool for create/close (ARCH-004 §4) and the client-side
 * submit_input_response payload. Server-side, the RPC re-validates answers
 * against the row's own questions (the authoritative check); these schemas are
 * the boundary validators consumers parse with first.
 */

import { z } from 'zod';
import { uuidSchema } from './common.js';

/**
 * JSONB schema version for the `input_requests.questions` payload (the sibling
 * of the `questions_schema_version` column, migration 0033, per ARCH-001 §3).
 * `answers` reuses this version because answers are validated against questions
 * (DATA-IR-03). Colocated here rather than in the central
 * `JSONB_SCHEMA_VERSIONS` map so this new module is self-contained and additive;
 * bump in lockstep with any `questions` shape change and the matching migration.
 */
export const INPUT_REQUEST_QUESTIONS_SCHEMA_VERSION = 1 as const;

/**
 * The four canonical question kinds (ARCH-028 §1). The design prototypes use
 * their own kind ids; the COPY mapping table maps those to these canonical
 * names. This enum is the single source of truth for the kind value set.
 */
export const inputRequestQuestionKindSchema = z.enum([
  'short_text',
  'long_text',
  'single_choice',
  'multi_choice',
]);
export type InputRequestQuestionKind = z.infer<
  typeof inputRequestQuestionKindSchema
>;

/** The two choice kinds that carry an `options` array. */
const CHOICE_KINDS: ReadonlyArray<InputRequestQuestionKind> = [
  'single_choice',
  'multi_choice',
];

function isChoiceKind(kind: InputRequestQuestionKind): boolean {
  return CHOICE_KINDS.includes(kind);
}

/**
 * One operator-authored question (DATA-IR-02). `options` is required and
 * non-empty (>= 2) for the two choice kinds and absent for the others; the
 * refinement below enforces that pairing. `hint` is an optional one-line helper
 * shown under the prompt. `order` positions the question in the rendered list.
 */
const inputRequestQuestionBaseSchema = z.object({
  id: z.string().min(1),
  prompt: z.string().min(1),
  hint: z.string().min(1).optional(),
  kind: inputRequestQuestionKindSchema,
  options: z.array(z.string().min(1)).min(2).optional(),
  required: z.boolean(),
  order: z.number().int().nonnegative(),
});

/**
 * The refined single-question schema (AC3): choice kinds MUST carry a
 * non-empty (>= 2) options array; non-choice kinds MUST NOT carry options.
 */
export const inputRequestQuestionSchema: z.ZodType<
  z.infer<typeof inputRequestQuestionBaseSchema>
> = inputRequestQuestionBaseSchema.superRefine((q, ctx) => {
  if (isChoiceKind(q.kind)) {
    if (q.options === undefined || q.options.length < 2) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['options'],
        message: `${q.kind} questions require an options array of at least 2 entries`,
      });
    }
  } else if (q.options !== undefined) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ['options'],
      message: `${q.kind} questions must not carry options`,
    });
  }
});
export type InputRequestQuestion = z.infer<typeof inputRequestQuestionSchema>;

/** The ordered question list (DATA-IR-02). At least one question. */
export const inputRequestQuestionsSchema: z.ZodType<InputRequestQuestion[]> = z
  .array(inputRequestQuestionSchema)
  .min(1);
export type InputRequestQuestions = z.infer<typeof inputRequestQuestionsSchema>;

/**
 * A single answer value (DATA-IR-03): a string for short_text / long_text /
 * single_choice, a string array for multi_choice.
 */
export const inputRequestAnswerSchema = z.union([
  z.string(),
  z.array(z.string()),
]);
export type InputRequestAnswer = z.infer<typeof inputRequestAnswerSchema>;

/** The answers map (DATA-IR-03): `{ [question_id]: string | string[] }`. */
export const inputRequestAnswersSchema: z.ZodType<
  Record<string, InputRequestAnswer>
> = z.record(z.string(), inputRequestAnswerSchema);
export type InputRequestAnswers = z.infer<typeof inputRequestAnswersSchema>;

/** The three context kinds (ARCH-028 §1). */
export const inputRequestContextTypeSchema = z.enum(['os', 'task', 'general']);
export type InputRequestContextType = z.infer<
  typeof inputRequestContextTypeSchema
>;

/**
 * create_input_request payload (TECH-IR-03). Operator-only. The context binding
 * is enforced here (task_id present for `task`, os_engagement_id present for
 * `os`, neither for `general`) and again by the row CHECK + RPC (migration
 * 0033/0035). Question authoring is operator-only, so the full question array
 * is part of this payload.
 */
const createInputRequestBaseSchema = z.object({
  subscriber_id: uuidSchema,
  context_type: inputRequestContextTypeSchema,
  title: z.string().min(1),
  intro: z.string().min(1).optional(),
  questions: inputRequestQuestionsSchema,
  task_id: uuidSchema.optional(),
  os_engagement_id: uuidSchema.optional(),
});

export const createInputRequestSchema: z.ZodType<
  z.infer<typeof createInputRequestBaseSchema>
> = createInputRequestBaseSchema.superRefine((v, ctx) => {
  if (v.context_type === 'task') {
    if (v.task_id === undefined) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['task_id'],
        message: 'task_id is required when context_type is "task"',
      });
    }
    if (v.os_engagement_id !== undefined) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['os_engagement_id'],
        message: 'os_engagement_id must be absent when context_type is "task"',
      });
    }
  } else if (v.context_type === 'os') {
    if (v.os_engagement_id === undefined) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['os_engagement_id'],
        message: 'os_engagement_id is required when context_type is "os"',
      });
    }
    if (v.task_id !== undefined) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['task_id'],
        message: 'task_id must be absent when context_type is "os"',
      });
    }
  } else {
    if (v.task_id !== undefined || v.os_engagement_id !== undefined) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['context_type'],
        message:
          'general requests carry neither task_id nor os_engagement_id',
      });
    }
  }
});
export type CreateInputRequest = z.infer<typeof createInputRequestSchema>;

/** close_input_request payload (TECH-IR-03). Operator-only. */
export const closeInputRequestSchema = z.object({
  request_id: uuidSchema,
});
export type CloseInputRequest = z.infer<typeof closeInputRequestSchema>;

/**
 * submit_input_response payload (TECH-IR-01). The recipient calls the RPC
 * directly via supabase-js rpc() as `authenticated`; this schema is the
 * client-side boundary validator. The authoritative validation (answers vs. the
 * row's own questions) runs server-side in the SECURITY DEFINER RPC (migration
 * 0035) -- this schema cannot see the questions, so it only shapes the payload.
 */
export const submitInputResponseSchema = z.object({
  request_id: uuidSchema,
  answers: inputRequestAnswersSchema,
});
export type SubmitInputResponse = z.infer<typeof submitInputResponseSchema>;

/**
 * Validate a recipient's answers against a known question list (AC1/AC3 mirror
 * of the RPC's server-side check, for client-side pre-validation in the form).
 * Returns the list of problems (empty = valid). The RPC remains the
 * authoritative gate; this is the optimistic client check so the recipient sees
 * field errors before the round-trip.
 *
 * Rules (mirrors migration 0035 submit_input_response):
 *   - required present: a required question must have a non-empty answer
 *     (multi_choice satisfies required only with a non-empty array).
 *   - option membership: every chosen option for a choice question must exist
 *     in that question's options.
 */
export interface AnswerValidationProblem {
  question_id: string;
  message: string;
}

export function validateInputAnswers(
  questions: InputRequestQuestions,
  answers: InputRequestAnswers,
): AnswerValidationProblem[] {
  const problems: AnswerValidationProblem[] = [];

  for (const q of questions) {
    const answer = answers[q.id];
    const present = answer !== undefined && answer !== null;

    if (q.required) {
      if (!present) {
        problems.push({ question_id: q.id, message: 'This question is required.' });
        continue;
      }
      if (q.kind === 'multi_choice') {
        if (!Array.isArray(answer) || answer.length === 0) {
          problems.push({
            question_id: q.id,
            message: 'Select at least one option.',
          });
          continue;
        }
      } else if (typeof answer !== 'string' || answer.trim().length === 0) {
        problems.push({ question_id: q.id, message: 'This question is required.' });
        continue;
      }
    }

    if (!present) continue;

    if (q.kind === 'single_choice') {
      if (typeof answer !== 'string') {
        problems.push({ question_id: q.id, message: 'Choose one option.' });
        continue;
      }
      if (
        answer.trim().length > 0 &&
        !(q.options ?? []).includes(answer)
      ) {
        problems.push({ question_id: q.id, message: 'Chosen option is not valid.' });
      }
    } else if (q.kind === 'multi_choice') {
      if (!Array.isArray(answer)) {
        problems.push({ question_id: q.id, message: 'Choose one or more options.' });
        continue;
      }
      const allowed = q.options ?? [];
      for (const choice of answer) {
        if (!allowed.includes(choice)) {
          problems.push({
            question_id: q.id,
            message: 'A chosen option is not valid.',
          });
          break;
        }
      }
    } else if (typeof answer !== 'string') {
      problems.push({ question_id: q.id, message: 'Answer must be text.' });
    }
  }

  return problems;
}
